import { useCallback, useEffect, useRef, useState } from "react";
import {
  listFilesApi,
  deleteFileApi,
  getFileQuotaApi,
  downloadFileApi,
  uploadFileXhr,
  type FileItem,
  type FileQuotaResponse,
} from "../services/api";

interface Toast {
  msg: string;
  type: "ok" | "err";
}

interface UseFileTransferReturn {
  files: FileItem[];
  total: number;
  quota: FileQuotaResponse | null;
  uploading: boolean;
  uploadProgress: number;
  error: string | null;
  toast: Toast | null;
  refresh: () => Promise<void>;
  upload: (file: File) => Promise<void>;
  download: (path: string) => Promise<void>;
  remove: (path: string) => Promise<void>;
}

const POLL_MS = 3000;

type EnforcedUploadLimits = FileQuotaResponse & {
  per_file_limit_bytes: number;
  session: FileQuotaResponse["session"] & {remaining_bytes: number};
};

function hasUsableUploadLimits(quota: FileQuotaResponse | null): quota is EnforcedUploadLimits {
  return quota !== null
    && typeof quota.per_file_limit_bytes === "number" && Number.isFinite(quota.per_file_limit_bytes)
    && quota.per_file_limit_bytes > 0
    && typeof quota.session.remaining_bytes === "number" && Number.isFinite(quota.session.remaining_bytes)
    && quota.session.remaining_bytes >= 0;
}

export function useFileTransfer(
  sessionId: string,
  sessionToken: string,
): UseFileTransferReturn {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [total, setTotal] = useState(0);
  const [quota, setQuota] = useState<FileQuotaResponse | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string, type: "ok" | "err") => {
    setToast({ msg, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const refresh = useCallback(async () => {
    if (!sessionId || document.hidden) return;
    try {
      const data = await listFilesApi(sessionId);
      setFiles(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "list error");
    }
  }, [sessionId]);

  const fetchQuota = useCallback(async () => {
    if (!sessionId) return;
    try {
      const q = await getFileQuotaApi(sessionId);
      setQuota(q);
    } catch {
      // quota display is best-effort
    }
  }, [sessionId]);

  const upload = useCallback(
    async (file: File) => {
      if (!sessionId || !sessionToken) return;
      // Client-side pre-validation is fail-closed: loading, fetch failures and
      // malformed responses must never turn an unknown operator limit into an
      // unlimited upload path. The server remains the authoritative gate.
      if (quota?.enabled !== false) {
        if (!hasUsableUploadLimits(quota)) {
          showToast("Upload limits unavailable — wait for quota and retry", "err");
          return;
        }
        if (file.size > quota.per_file_limit_bytes) {
          const limitMB = (quota.per_file_limit_bytes / 1024 / 1024).toFixed(0);
          showToast(`File too large (max ${limitMB} MB per file)`, "err");
          return;
        }
        if (file.size > quota.session.remaining_bytes) {
          showToast("Session quota exceeded — delete files or use a new session", "err");
          return;
        }
      }
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      try {
        await uploadFileXhr(sessionId, file, sessionToken, (pct) => setUploadProgress(pct));
        showToast(`${file.name} uploaded`, "ok");
        await Promise.all([refresh(), fetchQuota()]);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "upload failed";
        setError(msg);
        showToast(msg, "err");
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [sessionId, sessionToken, quota, refresh, fetchQuota, showToast],
  );

  const download = useCallback(
    async (path: string) => {
      if (!sessionId || !sessionToken) return;
      try {
        await downloadFileApi(sessionId, path, sessionToken);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "download failed";
        setError(msg);
        showToast(msg, "err");
      }
    },
    [sessionId, sessionToken, showToast],
  );

  const remove = useCallback(
    async (path: string) => {
      if (!sessionId) return;
      try {
        await deleteFileApi(sessionId, path);
        showToast("File deleted", "ok");
        await refresh();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "delete failed";
        setError(msg);
        showToast(msg, "err");
      }
    },
    [sessionId, refresh, showToast],
  );

  useEffect(() => {
    if (!sessionId) return;
    refresh();
    fetchQuota();
  }, [sessionId, refresh, fetchQuota]);

  useEffect(() => {
    if (!sessionId) return;
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [sessionId, refresh]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  return { files, total, quota, uploading, uploadProgress, error, toast, refresh, upload, download, remove };
}
